import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock, MapPin, User } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";

interface UpcomingAppointmentsProps {
  appointments: any[];
  isLoading: boolean;
}

export const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({ 
  appointments, 
  isLoading 
}) => {


  if (isLoading) {
    return (
      <Card className=" dark:bg-white">
        <CardHeader>
          <CardTitle className="flex items-center dark:text-gray-600">
            <Calendar className="mr-2 h-5 w-5" />
            Próximos Compromissos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const hasAppointments = Array.isArray(appointments) && appointments.length > 0;

  return (
    <Card className=" dark:bg-white">
      <CardHeader>
        <CardTitle className="flex items-center dark:text-gray-600">
          <Calendar className="mr-2 h-5 w-5" />
          Próximos Compromissos
          <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
            Debug: {appointments?.length || 0} items
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasAppointments ? (
          <div className="space-y-3">
            {appointments.map((appointment, index) => {
              
              try {
                const appointmentDate = new Date(appointment.date);
                const hasTime = appointment.time && appointment.time !== "00:00:00";
                
                return (
                  <div 
                    key={appointment.id || index} 
                    className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate dark:text-gray-600">
                        {appointment.title || "Sem título"}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Clock className="mr-1 h-3 w-3" />
                        {hasTime 
                          ? format(appointmentDate, "dd/MM 'às' HH:mm", { locale: ptBR })
                          : format(appointmentDate, "dd/MM", { locale: ptBR })
                        }
                        {hasTime && appointment.time && ` (${appointment.time})`}
                      </div>
                      
                      {appointment.clients?.name && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <User className="mr-1 h-3 w-3" />
                          {appointment.clients.name}
                        </div>
                      )}
                      
                      {appointment.location && (
                        <div className="flex items-center text-xs text-gray-500 mt-1">
                          <MapPin className="mr-1 h-3 w-3" />
                          {appointment.location}
                        </div>
                      )}
                    </div>
                    <span className="text-xs bg-lawyer-primary/10 text-lawyer-primary px-2 py-1 rounded-full">
                      {appointment.type || "Outro"}
                    </span>
                  </div>
                );
              } catch (error) {
                return (
                  <div key={appointment.id || index} className="p-3 border rounded-lg bg-red-50">
                    <p className="text-red-600 text-sm">Erro ao renderizar compromisso</p>
                    <pre className="text-xs mt-1 overflow-auto">
                      {JSON.stringify(appointment, null, 2)}
                    </pre>
                  </div>
                );
              }
            })}
            <Link 
              to="/dashboard/appointments" 
              className="block text-center text-sm text-lawyer-primary hover:underline mt-4"
            >
              Ver todos os compromissos
            </Link>
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500">
            <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <p className="text-sm">Nenhum compromisso próximo</p>
            {appointments && !Array.isArray(appointments) && (
              <pre className="text-xs mt-2 text-left bg-gray-100 p-2 rounded overflow-auto">
                {JSON.stringify(appointments, null, 2)}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};